import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Sidebar from "components/Sidebar/Sidebar";
import StorageUsage from "components/StorageUsage";
import { fetchArchivedCourses, requestArchiveCode, restoreArchivedCourse, verifyArchiveCode } from "services/storageService/storageService";
import { ArchivedCourse } from "services/storageService/storageService.types";
import {
  ActionButton,
  CategoryCard,
  CategoryGrid,
  CategoryName,
  CategoryValue,
  Content,
  Header,
  Page,
  Subtitle,
  Title,
  TitleBlock,
  CodeInput,
  ErrorText,
  FeatureCard,
  FeatureTitle,
  FormGrid,
} from "./StoragePage.styles";

const StorageArchivePage: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<ArchivedCourse[]>([]);
  const [message, setMessage] = useState("Загрузка архива");
  const [locked, setLocked] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [wait, setWait] = useState(0);

  const load = useCallback(() => {
    fetchArchivedCourses()
      .then((items) => {
        setCourses(items);
        setMessage(items.length ? "" : "В архиве пока нет курсов.");
      })
      .catch((error) => {
        if (error?.response?.status === 403) {
          setLocked(true);
          setMessage("");
          return;
        }
        setMessage("Не удалось загрузить архив.");
      });
  }, []);

  useEffect(() => { if (!wait) return; const timer = window.setTimeout(() => setWait((value) => Math.max(0, value - 1)), 1000); return () => window.clearTimeout(timer); }, [wait]);
  const sendCode = async () => { setError(""); try { const result = await requestArchiveCode(); setWait(result.retry_after || 0); } catch (e: any) { setError(e?.response?.data?.detail || "Не удалось отправить код."); setWait(e?.response?.data?.retry_after || 0); } };
  const unlock = async () => { setError(""); try { await verifyArchiveCode(code); setLocked(false); setCode(""); load(); } catch (e: any) { setError(e?.response?.data?.detail || "Код не принят."); setWait(e?.response?.data?.retry_after || 0); } };

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (courseId: number) => {
    await restoreArchivedCourse(courseId);
    load();
  };

  return (
    <Page>
      <Sidebar />
      <Content>
        <Header>
          <TitleBlock>
            <Title>Архив курсов</Title>
            <Subtitle>Раздел открыт после подтверждения кодом с почты школы</Subtitle>
          </TitleBlock>
          <ActionButton onClick={() => navigate("/storage")}>Вернуться в хранилище</ActionButton>
        </Header>
        <StorageUsage />
        {locked ? <FeatureCard><FeatureTitle>Архив защищён</FeatureTitle><Subtitle>Получите шестизначный код на почту школы. Код действует 5 минут, новый код можно запросить сразу. До подтверждения названия и содержимое курсов скрыты.</Subtitle><FormGrid><CodeInput aria-label="Код доступа к архиву" placeholder="Введите 6 цифр" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} /><ActionButton onClick={() => void sendCode()}>Получить код на почту</ActionButton><ActionButton $primary disabled={code.length !== 6} onClick={() => void unlock()}>Открыть архив</ActionButton></FormGrid>{error ? <ErrorText role="alert">{error}</ErrorText> : null}</FeatureCard> : null}
        {message ? <Subtitle>{message}</Subtitle> : null}
        {!locked ? <CategoryGrid>
          {courses.map((course) => (
            <CategoryCard key={course.id}>
              <CategoryName>{course.title}</CategoryName>
              <CategoryValue>{course.is_approved_by_admin ? "Скрыт от учеников на время архивации" : "Не опубликован"}</CategoryValue>
              <Subtitle>В архиве с {new Date(course.archived_at).toLocaleDateString("ru-RU")}</Subtitle>
              <Subtitle>Занимает {(course.size_bytes / 1024 / 1024 / 1024).toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ГБ</Subtitle>
              <ActionButton onClick={() => void restore(course.id)}>Вернуть курс</ActionButton>
            </CategoryCard>
          ))}
        </CategoryGrid> : null}
      </Content>
    </Page>
  );
};

export default StorageArchivePage;
